/**
 * Every word on the subcontractor packet page, in English and Spanish.
 *
 * The page is the only screen in Roof Board with a Spanish version (ADR 0279),
 * so its words live beside it rather than in a translation library nothing
 * else would use. The agreement itself is not here: it comes from the
 * `crew-packet` function, which prints the same text onto the signed PDF.
 * Neither are the error messages, which the function writes in the language
 * the page is in.
 */

export type PacketLanguage = "en" | "es";

export const WORDS = {
  en: {
    title: "Subcontractor packet",
    intro: (company: string) =>
      `${company} needs four things before you start work with us. It takes about ten minutes. When you sign, it comes straight back to us.`,
    steps: ["Tax form (W-9)", "Bank account", "Insurance", "Agreement"],
    loading: "Loading…",
    unreachable:
      "We could not load this page. Please check your connection and try again, or call us.",
    w9Title: "1. Tax form (W-9)",
    w9Hint:
      "This fills in the IRS Form W-9 for you. We need it to pay you and to send you a 1099.",
    legalName: "Your name, or your business's legal name",
    legalNameHint:
      "As it appears on your tax return. For a one-person business, your own name.",
    businessName: "Business name, if different",
    taxClass: "Type of business",
    taxClasses: {
      individual: "Individual / sole proprietor",
      llc: "Limited liability company (LLC)",
      c_corp: "C corporation",
      s_corp: "S corporation",
      partnership: "Partnership",
      trust: "Trust / estate",
      other: "Other",
    },
    llcCode: "How the LLC is taxed",
    llcCodes: {
      C: "C — as a C corporation",
      S: "S — as an S corporation",
      P: "P — as a partnership",
    },
    otherClass: "What kind of business",
    street: "Street address",
    city: "City",
    state: "State",
    zip: "ZIP",
    tinKind: "Tax number",
    ssn: "Social Security number",
    ein: "EIN (business tax number)",
    tinPlaceholder: { ssn: "123-45-6789", ein: "12-3456789" },
    backup:
      "The IRS has told me I am subject to backup withholding (most people are not — leave this unticked if you are unsure)",
    certification:
      "When you sign below, you also sign the W-9 and certify, under penalties of perjury, the four statements on it: that the number is correct, that you are not subject to backup withholding (unless you ticked the box), that you are a U.S. person, and that any FATCA code is correct.",
    viewForm: "Read the IRS form",
    bankTitle: "2. Bank account",
    bankHint: "Where we send your payments.",
    bankName: "Bank name",
    accountName: "Name on the account",
    routingNumber: "Routing number",
    routingHint: "Nine digits, bottom left of a check.",
    accountNumber: "Checking account number",
    accountNumberAgain: "Checking account number again",
    insuranceTitle: "3. Insurance",
    hasWc: "Do you carry workers' compensation insurance?",
    hasGl: "Do you carry general liability insurance?",
    yes: "Yes",
    no: "No",
    certificateTitle: "Certificate of insurance",
    certificate: (email: string, holder: string, address: string) =>
      `Ask your insurance agent to email your certificate of insurance to ${email}. The certificate holder is ${holder}, ${address}. Your general liability policy must name ${holder} as additional insured.`,
    agreementTitle: "4. Subcontractor agreement",
    agreementHint:
      "Please read it. The blanks fill in from your answers above when you sign.",
    agreementOther: "Lea el contrato en español",
    phone: "Your phone",
    email: "Your email",
    signerName: "Your full name",
    signerTitle: "Your title",
    signerTitleHint: "Owner, President, Manager…",
    companySigns: (name: string, title: string, company: string) =>
      `${name}, ${title}, signs for ${company} automatically when you sign.`,
    consent:
      "I have read the agreement and the W-9 above. I agree to sign them electronically, and typing my name is my signature.",
    sign: "Sign and send",
    signing: "Sending…",
    fix: "Some boxes need fixing. They are marked in red.",
    doneTitle: "Thank you. It's signed and sent.",
    done: (company: string) =>
      `${company} has your packet. Keep a copy for yourself:`,
    download: "Download your signed copy",
    doneCertificate: (email: string) =>
      `One more thing: ask your insurance agent to email your certificate of insurance to ${email}.`,
    language: "Language",
  },
  es: {
    title: "Paquete para subcontratistas",
    intro: (company: string) =>
      `${company} necesita cuatro cosas antes de que empiece a trabajar con nosotros. Toma unos diez minutos. Cuando firme, nos llega directamente.`,
    steps: [
      "Formulario de impuestos (W-9)",
      "Cuenta bancaria",
      "Seguros",
      "Contrato",
    ],
    loading: "Cargando…",
    unreachable:
      "No pudimos cargar esta página. Revise su conexión e intente de nuevo, o llámenos.",
    w9Title: "1. Formulario de impuestos (W-9)",
    w9Hint:
      "Esto llena el Formulario W-9 del IRS por usted. Lo necesitamos para pagarle y enviarle un 1099.",
    legalName: "Su nombre, o el nombre legal de su negocio",
    legalNameHint:
      "Como aparece en su declaración de impuestos. Si trabaja solo, su propio nombre.",
    businessName: "Nombre del negocio, si es diferente",
    taxClass: "Tipo de negocio",
    taxClasses: {
      individual: "Individuo / dueño único",
      llc: "Compañía de responsabilidad limitada (LLC)",
      c_corp: "Sociedad anónima de tipo C",
      s_corp: "Sociedad anónima de tipo S",
      partnership: "Sociedad colectiva",
      trust: "Fideicomiso / caudal hereditario",
      other: "Otro",
    },
    llcCode: "Cómo tributa la LLC",
    llcCodes: {
      C: "C — como sociedad anónima de tipo C",
      S: "S — como sociedad anónima de tipo S",
      P: "P — como sociedad colectiva",
    },
    otherClass: "Qué tipo de negocio",
    street: "Dirección",
    city: "Ciudad",
    state: "Estado",
    zip: "Código postal",
    tinKind: "Número de impuestos",
    ssn: "Número de Seguro Social",
    ein: "EIN (número de impuestos del negocio)",
    tinPlaceholder: { ssn: "123-45-6789", ein: "12-3456789" },
    backup:
      "El IRS me ha notificado que estoy sujeto a la retención adicional (a la mayoría no le aplica — no marque esta casilla si no está seguro)",
    certification:
      "Cuando firme abajo, también firma el W-9 y certifica, bajo pena de perjurio, las cuatro declaraciones que contiene: que el número es correcto, que no está sujeto a la retención adicional (salvo que haya marcado la casilla), que es una persona de los EE. UU., y que cualquier código FATCA es correcto.",
    viewForm: "Ver el formulario del IRS",
    bankTitle: "2. Cuenta bancaria",
    bankHint: "Donde le enviamos sus pagos.",
    bankName: "Nombre del banco",
    accountName: "Nombre en la cuenta",
    routingNumber: "Número de ruta",
    routingHint: "Nueve dígitos, abajo a la izquierda de un cheque.",
    accountNumber: "Número de cuenta de cheques",
    accountNumberAgain: "Número de cuenta de cheques otra vez",
    insuranceTitle: "3. Seguros",
    hasWc: "¿Tiene seguro de compensación para trabajadores?",
    hasGl: "¿Tiene seguro de responsabilidad civil general?",
    yes: "Sí",
    no: "No",
    certificateTitle: "Certificado de seguro",
    certificate: (email: string, holder: string, address: string) =>
      `Pida a su agente de seguros que envíe su certificado de seguro por correo a ${email}. El titular del certificado es ${holder}, ${address}. Su póliza de responsabilidad civil general debe nombrar a ${holder} como asegurado adicional.`,
    agreementTitle: "4. Contrato de subcontratista",
    agreementHint:
      "Por favor léalo. Los espacios en blanco se llenan con sus respuestas de arriba cuando firme.",
    agreementOther: "Read the agreement in English",
    phone: "Su teléfono",
    email: "Su correo electrónico",
    signerName: "Su nombre completo",
    signerTitle: "Su cargo",
    signerTitleHint: "Dueño, Presidente, Gerente…",
    companySigns: (name: string, title: string, company: string) =>
      `${name}, ${title}, firma por ${company} automáticamente cuando usted firme.`,
    consent:
      "He leído el contrato y el W-9 de arriba. Acepto firmarlos electrónicamente, y escribir mi nombre es mi firma.",
    sign: "Firmar y enviar",
    signing: "Enviando…",
    fix: "Algunas casillas necesitan corrección. Están marcadas en rojo.",
    doneTitle: "Gracias. Está firmado y enviado.",
    done: (company: string) =>
      `${company} ya tiene su paquete. Guarde una copia para usted:`,
    download: "Descargar su copia firmada",
    doneCertificate: (email: string) =>
      `Una cosa más: pida a su agente de seguros que envíe su certificado de seguro por correo a ${email}.`,
    language: "Idioma",
  },
} as const;
